import { useFormState } from "react-hook-form";
import { INCENTIVE_AFFECTING_FIELDS } from "@/constants";

type IncentiveImpact = {
  /** Names of incentive-affecting fields the user has changed since the form was loaded. */
  impactedFields: string[];
  /** Whether any incentive-affecting field has been changed. */
  hasIncentiveImpact: boolean;
  isFieldImpacted: (name: string) => boolean;
};

/**
 * Tracks which incentive-affecting fields have been edited in the current form session.
 *
 * SDCP-1305: editing a project no longer recalculates the incentive, so the form has to tell
 * the admin when their change has made the stored amount stale. Dirty state is measured
 * against the values the form was reset with, i.e. what is currently saved on the project.
 *
 * @param enabled pass false in "create" mode, where every field is dirty by definition and
 *                there is no approved amount to go stale.
 */
export const useIncentiveImpactedFields = (enabled = true): IncentiveImpact => {
  const { dirtyFields } = useFormState();

  // Recomputed every render on purpose: react-hook-form mutates `dirtyFields` in place, so
  // its identity is not a reliable memo key — caching on it misses every field marked dirty
  // after the first. Fourteen property lookups is cheaper than getting this wrong.
  const impactedFields: string[] = enabled
    ? INCENTIVE_AFFECTING_FIELDS.filter((field) =>
        Boolean(dirtyFields[field as keyof typeof dirtyFields])
      ).slice()
    : [];

  return {
    impactedFields,
    hasIncentiveImpact: impactedFields.length > 0,
    isFieldImpacted: (name: string) => impactedFields.includes(name),
  };
};

export default useIncentiveImpactedFields;
